import { useCallback, useEffect, useRef, useState } from 'react';
import { withKey, API_BASE } from '../services/utils';
import { computeOfferHash, isRowNonEmpty } from './useOfferState';

export function useAutoSave({
	offer,
	offerWithCalcRef,
	setOffer,
	normalizeLoadedOffer,
	invoiceCurrency,
	currencyRates,
	currentRates,
	isNewDealFlow = false,
	totalCurrencyRef,
	totalAmountInCurrencyRef,
}) {
	const [hasInitialSave, setHasInitialSave] = useState(false);
	const [autoSaveActive, setAutoSaveActive] = useState(false);
	const [isSavingZoho, setIsSavingZoho] = useState(false);
	const [lastSaveTime, setLastSaveTime] = useState(null);

	const lastZohoSyncHashRef = useRef(null);
	const previousHashRef = useRef(null);
	const localSaveIntervalRef = useRef(null);
	const zohoSyncIntervalRef = useRef(null);
	const offerRef = useRef(offer);
	const invoiceCurrencyRef = useRef(invoiceCurrency);
	const currencyRatesRef = useRef(currencyRates);
	const currentRatesRef = useRef(currentRates);
	const initialSaveInProgressRef = useRef(false);
	const initialSaveDebounceRef = useRef(null);
	const pendingSyncKeyRef = useRef(null);
	const pendingOfferUpdateRef = useRef(null);
	const cachedSyncUrlRef = useRef(null);

	// Apply a setOffer update immediately if no table cell is focused,
	// otherwise defer it so the user's current typing is not interrupted.
	const applyOrDefer = useCallback((updateFn) => {
		if (document.activeElement?.dataset?.col) {
			pendingOfferUpdateRef.current = updateFn;
		} else {
			setOffer(updateFn);
			pendingOfferUpdateRef.current = null;
		}
	}, [setOffer]);

	// Called when the user blurs out of a table cell – drains any deferred update.
	const flushPendingUpdate = useCallback(() => {
		if (pendingOfferUpdateRef.current) {
			setOffer(pendingOfferUpdateRef.current);
			pendingOfferUpdateRef.current = null;
		}
	}, [setOffer]);

	// Build a safe setOffer updater from a server response.
	// Keeps prev.items as the base (never overwrites what the user typed);
	// only patches zoho_line_item_id onto the matching item.
	//
	// Matching is by the row's local `id`, NOT by array position. applyOrDefer
	// can hold this updater until the user blurs a cell, and rows can be added,
	// deleted, moved or bulk-inserted (copy-from-offer) in the meantime. Index
	// matching then wrote each Zoho row id onto the wrong line — and two lines
	// could end up sharing one id, after which the next save overwrote a foreign
	// Zoho row. See docs/known-issues.md ("Row drift").
	const buildSyncUpdate = useCallback((data) => (prev) => {
		const hiddenFlags = {};
		for (const item of prev.items) {
			if (item.product_id && item.product_hidden) hiddenFlags[item.product_id] = true;
		}
		const serverItems = data.items || [];
		const serverIdsByRowId = new Map();
		for (const serverItem of serverItems) {
			if (serverItem?.id === undefined || serverItem?.id === null) continue;
			if (serverItem.zoho_line_item_id === undefined || serverItem.zoho_line_item_id === null) continue;
			serverIdsByRowId.set(String(serverItem.id), serverItem.zoho_line_item_id);
		}
		if (serverItems.length > 0 && serverIdsByRowId.size === 0) {
			// Falling back to index matching here would reintroduce the bug this
			// replaced, so we do not. Say so instead of silently never assigning
			// ids, which would make every save recreate the Zoho rows.
			console.warn('Zoho sync: server returned no row ids to match on; line item ids not updated');
		}
		// Never let two rows claim the same Zoho subform row.
		const claimed = new Set();
		const safeItems = prev.items.map((item) => {
			const sid = serverIdsByRowId.get(String(item.id));
			if (sid === undefined || claimed.has(String(sid))) return item;
			claimed.add(String(sid));
			return { ...item, zoho_line_item_id: sid };
		});
		const { items: _ignored, ...dataRest } = data;
		const normalized = normalizeLoadedOffer({ ...prev, ...dataRest, items: safeItems });
		normalized.items = normalized.items.map(item =>
			item.product_id && hiddenFlags[item.product_id]
				? { ...item, product_hidden: true, hidden: true }
				: item
		);
		return normalized;
	}, [normalizeLoadedOffer]);


	useEffect(() => {
		const prevFxEUR = offerRef.current?.fx_rates?.EUR;
		const newFxEUR = offer?.fx_rates?.EUR;
		const prevCurrEUR = currencyRatesRef.current?.EUR;
		const newCurrEUR = currencyRates?.EUR;
		if (prevFxEUR !== newFxEUR) console.log('[FX-DEBUG] useAutoSave ref sync: offer.fx_rates.EUR changed', prevFxEUR, '->', newFxEUR);
		if (prevCurrEUR !== newCurrEUR) console.log('[FX-DEBUG] useAutoSave ref sync: currencyRates.EUR changed', prevCurrEUR, '->', newCurrEUR);
		offerRef.current = offer;
		invoiceCurrencyRef.current = invoiceCurrency;
		currencyRatesRef.current = currencyRates;
		currentRatesRef.current = currentRates;
	}, [offer, invoiceCurrency, currencyRates, currentRates]);

	// Keep a pre-cached authenticated URL for synchronous beforeunload saves.
	// sendBeacon cannot do async work, so the URL must be ready before the page closes.
	useEffect(() => {
		let cancelled = false;
		const refreshUrl = async () => {
			const q = offerRef.current;
			if (q?.id) {
				const url = await withKey(`${API_BASE}/api/zoho/offers/${q.id}`);
				if (!cancelled) cachedSyncUrlRef.current = url;
			}
		};
		refreshUrl();
		const interval = setInterval(refreshUrl, 30000);
		return () => { cancelled = true; clearInterval(interval); };
	}, [offer.id]);

	const prepareOfferData = useCallback(() => {
		const q = (offerWithCalcRef && offerWithCalcRef.current) ? offerWithCalcRef.current : offerRef.current;
		const invCurr = invoiceCurrencyRef.current;
		const rates = currencyRatesRef.current;
		const latestRates = currentRatesRef.current;
		const snapshotRate = rates?.[invCurr] || 0;
		const latestRate = latestRates?.[invCurr] || snapshotRate;
		const exchDiff = latestRate - snapshotRate;
		const exchDiffPercent = snapshotRate > 0 ? ((latestRate - snapshotRate) / snapshotRate) * 100 : 0;
		const exchRate10DKK = invCurr === 'DKK' ? 1 : (rates?.[invCurr] ? 10 / (rates[invCurr] / 100) : 0);
		const fullAmountInCurrency = invCurr === 'DKK' ? q.total : (rates?.[invCurr] ? q.total / (rates[invCurr] / 100) : 0);

		// Restore each item's unit_price to the base value (before
		// total_vat_adjustment_expr adjustment) so that on reload the
		// adjustment expression isn't applied twice.
		const items = (q.items || []).map(item => {
			if (item.unit_price_base !== undefined && item.unit_price_base !== null
				&& item.total_vat_adjustment_expr) {
				return { ...item, unit_price: item.unit_price_base };
			}
			return item;
		});

		return {
			...q,
			items,
			inv_currency: invCurr,
			exch_rate_10_dkk: exchRate10DKK,
			full_amount_in_currency: fullAmountInCurrency,
			original_rate: snapshotRate,
			latest_saved_rate: latestRate,
			exch_diff: exchDiff,
			exch_diff_percent: exchDiffPercent,
			total_currency: totalCurrencyRef?.current || 'DKK',
			total_amount_in_currency: totalAmountInCurrencyRef?.current || 0,
		};
	}, [offerWithCalcRef]); // eslint-disable-line react-hooks/exhaustive-deps

	const saveToLocalDB = useCallback(async () => {
		const q = offerRef.current;
		if (!q.id) return;

		try {
			const offerData = prepareOfferData();
			const url = await withKey(`${API_BASE}/api/localdb/offers/${q.id}`);
			const response = await fetch(url, {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(offerData),
			});
			if (response.ok) {
				setLastSaveTime(new Date());
			}
		} catch (error) {
			console.error('Auto-save to local DB failed:', error);
		}
	}, [prepareOfferData]);

	const getCurrentHash = useCallback(() => {
		const q = offerRef.current;
		return computeOfferHash(q.items, invoiceCurrencyRef.current, q.discount, q.fx_fetched_at);
	}, []);

	const syncToZoho = useCallback(async (options = {}) => {
		const { keepalive = false, silent = false, force = false } = options;
		const q = offerRef.current;
		if (!q.id) return;

		const currentHash = getCurrentHash();

		if (!force && lastZohoSyncHashRef.current && currentHash === lastZohoSyncHashRef.current) {
			if (!silent) {
				console.log('Zoho sync skipped - no changes detected');
			}
			return;
		}

		if (!silent) setIsSavingZoho(true);
		try {
			const offerData = prepareOfferData();
			const url = await withKey(`${API_BASE}/api/zoho/offers/${q.id}`);
			const response = await fetch(url, {
				method: 'PUT',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(offerData),
				keepalive,
			});

			if (!keepalive) {
				const data = await response.json();
				if (!data.error) {
					if (data.items) {
						applyOrDefer(buildSyncUpdate(data));
					}
					lastZohoSyncHashRef.current = currentHash;
					if (!silent) console.log('Zoho sync completed successfully');
				} else {
					console.error('Zoho sync error:', data.error);
				}
			} else if (response.ok) {
				lastZohoSyncHashRef.current = currentHash;
			}
		} catch (error) {
			if (!silent) console.error('Zoho sync failed:', error);
		}
		if (!silent) setIsSavingZoho(false);
	}, [getCurrentHash, prepareOfferData, applyOrDefer, buildSyncUpdate]);

	const performInitialSave = useCallback(async () => {
		if (initialSaveInProgressRef.current) {
			console.log('Initial save already in progress, skipping');
			return;
		}

		const q = offerRef.current;
		const hasProduct = q.items.some(item => item.product_id);
		if (!hasProduct) return;

		initialSaveInProgressRef.current = true;
		setIsSavingZoho(true);

		try {
			const offerData = prepareOfferData();
			const isUpdate = !!q.id;
			const url = isUpdate
				? await withKey(`${API_BASE}/api/zoho/offers/${q.id}`)
				: await withKey(`${API_BASE}/api/zoho/offers`);

			const response = await fetch(url, {
				method: isUpdate ? 'PUT' : 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(offerData),
			});
			const data = await response.json();
			if (!data.error) {
				applyOrDefer(buildSyncUpdate(data));
				setHasInitialSave(true);
				const hash = computeOfferHash(data.items || q.items, invoiceCurrencyRef.current, q.discount, data.fx_fetched_at || q.fx_fetched_at);
				lastZohoSyncHashRef.current = hash;
				previousHashRef.current = hash;
				console.log('Initial save completed - offer ID:', data.id);
			} else {
				console.error('Initial save error:', data.error);
				initialSaveInProgressRef.current = false;
			}
		} catch (error) {
			console.error('Initial save failed:', error);
			initialSaveInProgressRef.current = false;
		}
		setIsSavingZoho(false);
	}, [prepareOfferData, applyOrDefer, buildSyncUpdate]);

	useEffect(() => {
		const hasProduct = offer.items.some(item => item.product_id);
		if (!hasProduct) return;

		const currentHash = computeOfferHash(offer.items, invoiceCurrency, offer.discount, offer.fx_fetched_at);

		if (!previousHashRef.current) {
			previousHashRef.current = currentHash;
			if (offer.id && offer.zoho_id) {
				setHasInitialSave(true);
				initialSaveInProgressRef.current = true;
				lastZohoSyncHashRef.current = currentHash;
			}
			return;
		}

		if (currentHash !== previousHashRef.current) {
			previousHashRef.current = currentHash;
			setAutoSaveActive(true);

			// When user arrived via deal_id (new offer), skip auto-creation.
			// The user must click "Create Offer" explicitly.
			if (!isNewDealFlow) {
				const needsInitialSave = (!offer.id && !hasInitialSave && !initialSaveInProgressRef.current);
				if (needsInitialSave) {
					if (initialSaveDebounceRef.current) {
						clearTimeout(initialSaveDebounceRef.current);
					}
					initialSaveDebounceRef.current = setTimeout(() => {
						performInitialSave();
					}, 500);
				}
			}
		}
	}, [offer.items, offer.id, offer.zoho_id, offer.discount, offer.fx_fetched_at, invoiceCurrency, hasInitialSave, performInitialSave, isNewDealFlow]);

	useEffect(() => {
		const q = offer;
		if (!q?.id) return;
		const key = `pendingZohoSync:${q.id}`;
		pendingSyncKeyRef.current = key;
		let pendingHash = null;
		try {
			pendingHash = localStorage.getItem(key);
		} catch (error) {
			pendingHash = null;
		}
		if (!pendingHash) return;

		const currentHash = computeOfferHash(q.items, invoiceCurrency, q.discount, q.fx_fetched_at);
		if (currentHash === pendingHash) {
			syncToZoho({ force: true });
		} else {
			syncToZoho({ force: true });
		}
		try {
			localStorage.removeItem(key);
		} catch (error) {
			// Ignore storage errors
		}
	}, [offer, invoiceCurrency, syncToZoho]);

	useEffect(() => {
		if (!autoSaveActive || !offer.id) {
			if (localSaveIntervalRef.current) {
				clearInterval(localSaveIntervalRef.current);
				localSaveIntervalRef.current = null;
			}
			return;
		}

		localSaveIntervalRef.current = setInterval(() => {
			saveToLocalDB();
		}, 5000);

		saveToLocalDB();

		return () => {
			if (localSaveIntervalRef.current) {
				clearInterval(localSaveIntervalRef.current);
				localSaveIntervalRef.current = null;
			}
		};
	}, [autoSaveActive, offer.id, saveToLocalDB]);

	useEffect(() => {
		if (!autoSaveActive || !offer.id) {
			if (zohoSyncIntervalRef.current) {
				clearInterval(zohoSyncIntervalRef.current);
				zohoSyncIntervalRef.current = null;
			}
			return;
		}

		zohoSyncIntervalRef.current = setInterval(() => {
			syncToZoho();
		}, 30 * 1000);

		return () => {
			if (zohoSyncIntervalRef.current) {
				clearInterval(zohoSyncIntervalRef.current);
				zohoSyncIntervalRef.current = null;
			}
		};
	}, [autoSaveActive, offer.id, syncToZoho]);

	useEffect(() => {
		const handleBeforeUnload = () => {
			const q = offerRef.current;
			if (!q?.id) return;
			const currentHash = getCurrentHash();
			if (lastZohoSyncHashRef.current && currentHash === lastZohoSyncHashRef.current) return;

			// localStorage fallback — if the beacon fails or the next page load
			// detects this hash, it will force a Zoho sync.
			const key = pendingSyncKeyRef.current || `pendingZohoSync:${q.id}`;
			try {
				localStorage.setItem(key, currentHash);
			} catch (error) {
				// Ignore storage errors
			}

			// Synchronous save via navigator.sendBeacon.
			// Unlike the previous async fetch+keepalive approach, sendBeacon is
			// fully synchronous and guaranteed to be queued before page unload.
			const url = cachedSyncUrlRef.current;
			if (url) {
				const offerData = prepareOfferData();
				const blob = new Blob(
					[JSON.stringify(offerData)],
					{ type: 'application/json' }
				);
				navigator.sendBeacon(url, blob);
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [getCurrentHash, prepareOfferData]);

	useEffect(() => () => {
		if (localSaveIntervalRef.current) clearInterval(localSaveIntervalRef.current);
		if (zohoSyncIntervalRef.current) clearInterval(zohoSyncIntervalRef.current);
		if (initialSaveDebounceRef.current) clearTimeout(initialSaveDebounceRef.current);
	}, []);

	return {
		isSavingZoho,
		lastSaveTime,
		syncToZoho,
		performInitialSave,
		hasInitialSave,
		flushPendingUpdate
	};
}
